/**
 * BookReader — Fullscreen-Modal mit Modern-Glossy Page-Flip.
 *
 * Props:
 *   - guide: LEITFAEDEN-Eintrag (title, h1, intro, trust, stats, quote, etc.)
 *   - slug: string  (für Vollversions-CTA-Link)
 *   - onClose: () => void
 *
 * UX:
 *   - Tastatur: ← / → zum Blättern · Esc zum Schließen
 *   - Buttons unten + große Klick-Zonen links/rechts
 *   - Fortschritts-Balken oben
 *   - Doppel-Seiten auf Desktop, Einzel-Seite auf Mobile
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, ArrowRight, Sparkles, ShieldCheck, Clock4 } from "lucide-react";

const ICONS = { clock: Clock4, shield: ShieldCheck, sparkles: Sparkles };

function buildPages(guide, slug) {
  const pages = [];

  // Page 0 · TITELSEITE
  pages.push({
    kind: "cover",
    kicker: guide.kicker,
    h1a: guide.h1a,
    h1b: guide.h1b,
    slug,
  });

  // Page 1 · WORUM ES GEHT (Intro)
  pages.push({
    kind: "content",
    chapter: "Kapitel 1",
    heading: "Worum es geht",
    body: guide.intro,
    quote: guide.proof || null,
  });

  // Page 2 · WAS DU BEKOMMST (alle 3 Trust-Items zusammen, kompakt)
  if (guide.trust && guide.trust.length) {
    pages.push({
      kind: "trust-list",
      chapter: "Kapitel 2",
      heading: "Was du bekommst",
      items: guide.trust,
    });
  }

  // Page 3 · AUF EINEN BLICK (Stats + Quote gemeinsam)
  if (guide.stats && guide.stats.length) {
    pages.push({
      kind: "stats-quote",
      chapter: "Kapitel 3",
      heading: "Auf einen Blick",
      stats: guide.stats,
      quote: guide.quote,
      author: guide.quoteAuthor,
    });
  }

  // Page 4 · UPDATE (falls whatsNew)
  if (guide.whatsNew) {
    pages.push({
      kind: "update",
      chapter: "Aktualisierung",
      heading: guide.whatsNew.title,
      version: guide.whatsNew.version,
      bullets: guide.whatsNew.bullets,
    });
  }

  // Page 5 · WIE ES WEITERGEHT (Bridge)
  if (guide.bridge) {
    pages.push({
      kind: "content",
      chapter: "Kapitel 4",
      heading: "Wie es weitergeht",
      body: guide.bridge,
    });
  }

  // Page 6 · CTA (Rückumschlag)
  pages.push({
    kind: "cta",
    slug,
    kicker: guide.kicker,
  });

  return pages;
}

export default function BookReader({ guide, slug, onClose }) {
  const pages = useMemo(() => buildPages(guide, slug), [guide, slug]);
  const [idx, setIdx] = useState(0);
  const [flipping, setFlipping] = useState(null); // 'next' | 'prev' | null
  const containerRef = useRef(null);

  const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 900px)").matches;
  const step = isDesktop ? 2 : 1;

  const go = useCallback((direction) => {
    setFlipping((cur) => cur || direction);
    setTimeout(() => {
      setIdx((v) => {
        if (direction === "next") return Math.min(v + step, pages.length - 1);
        return Math.max(v - step, 0);
      });
      setFlipping(null);
    }, 320);
  }, [pages.length, step]);

  const canPrev = idx > 0;
  const canNext = idx + step < pages.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight" && canNext) go("next");
      else if (e.key === "ArrowLeft" && canPrev) go("prev");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, canNext, canPrev, onClose]);

  // Prevent body scroll while reader is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const progress = Math.round(((idx + 1) / pages.length) * 100);
  const currentPageNumber = idx + 1;
  const totalPages = pages.length;

  const leftPage = pages[idx];
  const rightPage = isDesktop ? pages[idx + 1] : null;

  return (
    <div
      ref={containerRef}
      data-testid="book-reader"
      className="fixed inset-0 z-[200] bg-brand-darkred/40 backdrop-blur-md flex flex-col"
    >
      {/* Progress + Close */}
      <div className="shrink-0 flex items-center gap-4 px-4 md:px-8 pt-4 pb-3 bg-cream-50/95 backdrop-blur border-b border-cream-300">
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-terra truncate">
            {guide.kicker}
          </div>
          <div className="mt-1 h-[3px] w-full bg-cream-200 rounded-full overflow-hidden">
            <div className="h-full bg-terra transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="text-[11px] text-sand-600 whitespace-nowrap tabular-nums font-medium">
          Seite {currentPageNumber}{isDesktop && idx + 1 < totalPages ? `–${Math.min(idx + 2, totalPages)}` : ""} / {totalPages}
        </div>
        <button
          onClick={onClose}
          data-testid="book-reader-close"
          className="h-10 w-10 rounded-full bg-cream-100 hover:bg-cream-200 text-sand-800 inline-flex items-center justify-center transition-colors border border-cream-300"
          aria-label="Reader schließen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Book Area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative select-none">
        {/* Klickzone links */}
        <button
          onClick={() => canPrev && go("prev")}
          disabled={!canPrev}
          data-testid="book-reader-prev"
          aria-label="Zurück blättern"
          className={`absolute left-0 top-0 bottom-0 w-1/4 z-10 flex items-center justify-start pl-4 md:pl-8 transition-opacity ${
            canPrev ? "cursor-pointer opacity-100" : "opacity-25 cursor-not-allowed"
          }`}
        >
          <span className="h-11 w-11 rounded-full bg-cream-50 hover:bg-white text-sand-800 inline-flex items-center justify-center transition-colors shadow-soft border border-cream-300">
            <ChevronLeft className="h-5 w-5" />
          </span>
        </button>
        {/* Klickzone rechts */}
        <button
          onClick={() => canNext && go("next")}
          disabled={!canNext}
          data-testid="book-reader-next"
          aria-label="Weiter blättern"
          className={`absolute right-0 top-0 bottom-0 w-1/4 z-10 flex items-center justify-end pr-4 md:pr-8 transition-opacity ${
            canNext ? "cursor-pointer opacity-100" : "opacity-25 cursor-not-allowed"
          }`}
        >
          <span className="h-11 w-11 rounded-full bg-cream-50 hover:bg-white text-sand-800 inline-flex items-center justify-center transition-colors shadow-soft border border-cream-300">
            <ChevronRight className="h-5 w-5" />
          </span>
        </button>

        {/* Book */}
        <div
          className="relative mx-auto"
          style={{
            width: isDesktop ? "min(1080px, 92vw)" : "min(560px, 94vw)",
            height: "min(720px, 82vh)",
          }}
        >
          <div
            className={`relative w-full h-full flex bg-cream-50 rounded-[4px] shadow-[0_30px_60px_-15px_rgba(82,42,37,0.35)] ring-1 ring-cream-300 ${
              flipping === "next" ? "book-flip-next" : ""
            } ${flipping === "prev" ? "book-flip-prev" : ""}`}
            style={{ perspective: "2000px" }}
          >
            <BookPage page={leftPage} slug={slug} side={isDesktop ? "left" : "single"} />
            {isDesktop && (
              <>
                <div className="w-px bg-gradient-to-b from-transparent via-cream-300 to-transparent" aria-hidden />
                <BookPage page={rightPage} slug={slug} side="right" />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="shrink-0 flex items-center justify-center gap-3 px-4 py-4 bg-cream-50/95 backdrop-blur border-t border-cream-300">
        <button
          onClick={() => canPrev && go("prev")}
          disabled={!canPrev}
          data-testid="book-reader-prev-btn"
          className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-cream-100 disabled:opacity-30 text-sand-800 px-5 h-10 text-[13px] font-semibold transition-colors border border-cream-300"
        >
          <ChevronLeft className="h-4 w-4" /> Zurück
        </button>
        <button
          onClick={() => canNext && go("next")}
          disabled={!canNext}
          data-testid="book-reader-next-btn"
          className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark disabled:opacity-30 text-white px-5 h-10 text-[13px] font-semibold transition-colors"
        >
          Weiter <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <style>{`
        @keyframes bookFlipNext {
          from { transform: rotateY(0deg); }
          to   { transform: rotateY(-2deg); }
        }
        @keyframes bookFlipPrev {
          from { transform: rotateY(0deg); }
          to   { transform: rotateY(2deg); }
        }
        .book-flip-next { animation: bookFlipNext 0.32s ease-in-out; transform-origin: center left; }
        .book-flip-prev { animation: bookFlipPrev 0.32s ease-in-out; transform-origin: center right; }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BookPage — eine einzelne Buchseite (Skandi-Papier · Cream · warme Akzente)
// ---------------------------------------------------------------------------
function BookPage({ page, slug, side }) {
  if (!page) {
    return <div className="flex-1 bg-cream-50 rounded-[4px]" />;
  }

  const shadowSide = side === "left"
    ? "shadow-[inset_-16px_0_28px_-18px_rgba(82,42,37,0.18)]"
    : side === "right"
      ? "shadow-[inset_16px_0_28px_-18px_rgba(82,42,37,0.18)]"
      : "";

  return (
    <div
      className={`flex-1 flex flex-col ${shadowSide} bg-cream-50 overflow-hidden`}
      data-testid={`book-page-${page.kind}`}
    >
      {renderPage(page, slug)}
    </div>
  );
}

function renderPage(page, slug) {
  switch (page.kind) {
    case "cover":       return <CoverPage page={page} />;
    case "content":     return <ContentPage page={page} />;
    case "trust-list":  return <TrustListPage page={page} />;
    case "stats-quote": return <StatsQuotePage page={page} />;
    case "update":      return <UpdatePage page={page} />;
    case "cta":         return <CtaPage slug={slug} kicker={page.kicker} />;
    default:            return <ContentPage page={{ heading: "", body: "" }} />;
  }
}

// TITELSEITE — echtes Cover-Bild als Hero, Titel darüber
function CoverPage({ page }) {
  return (
    <div className="flex-1 relative flex flex-col justify-between overflow-hidden bg-peach-50">
      {page.slug && (
        <img
          src={`/covers/${page.slug}.png`}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* leichte helle Vignette für Text-Lesbarkeit */}
      <div className="absolute inset-0 bg-gradient-to-b from-cream-50/60 via-cream-50/10 to-cream-50/85" />

      <div className="relative p-8 md:p-14">
        <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-brand-darkred">
          Dickies-Campus · Bibliothek
        </div>
        <div className="mt-3 h-px w-12 bg-terra" />
      </div>

      <div className="relative p-8 md:p-14">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-semibold text-brand-darkred mb-4">
          {page.kicker}
        </div>
        <h1 className="font-display text-[28px] md:text-[42px] leading-[1.05] tracking-tight font-normal text-brand-darkred max-w-[90%]">
          {page.h1a}
          {page.h1b && <><br /><span className="text-terra italic">{page.h1b}</span></>}
        </h1>
      </div>

      <div className="relative p-8 md:p-14 text-[11px] uppercase tracking-[0.24em] font-semibold text-brand-darkred/70">
        Ausgabe 2026
      </div>
    </div>
  );
}

// KAPITEL-KOPF (klein, unauffällig)
function PageHeader({ chapter, heading }) {
  return (
    <>
      {chapter && (
        <div className="text-[9.5px] uppercase tracking-[0.28em] font-bold text-terra mb-1.5">
          {chapter}
        </div>
      )}
      {heading && (
        <h2 className="font-display text-[19px] md:text-[22px] leading-[1.25] tracking-tight text-sand-800 mb-4">
          {heading}
        </h2>
      )}
      <div className="h-px w-10 bg-terra/40 mb-5" />
    </>
  );
}

// INHALTS-SEITE · normaler Fließtext, wie im Buch
function ContentPage({ page }) {
  const hasIntro = page.chapter === "Kapitel 1";
  return (
    <div className="flex-1 flex flex-col p-8 md:p-12">
      <PageHeader chapter={page.chapter} heading={page.heading} />
      {page.body && (
        <p className="text-[13.5px] md:text-[14.5px] leading-[1.75] text-sand-700 text-justify hyphens-auto">
          {hasIntro && (
            <span className="float-left font-display text-[52px] leading-[0.85] text-terra pr-2 pt-1">
              {page.body.trim().charAt(0)}
            </span>
          )}
          {hasIntro ? page.body.trim().slice(1) : page.body}
        </p>
      )}
      {page.quote && (
        <p className="mt-5 text-[12px] leading-[1.6] text-sand-500 italic border-l-2 border-terra/40 pl-3">
          {page.quote}
        </p>
      )}
    </div>
  );
}

// TRUST-LISTE · 3 Punkte kompakt auf einer Seite
function TrustListPage({ page }) {
  return (
    <div className="flex-1 flex flex-col p-8 md:p-12">
      <PageHeader chapter={page.chapter} heading={page.heading} />
      <ul className="space-y-4">
        {page.items.map((t, i) => {
          const Icon = ICONS[t.icon] || Sparkles;
          return (
            <li key={i} className="flex gap-3">
              <span className="mt-0.5 h-8 w-8 shrink-0 rounded-full bg-terra/12 text-terra inline-flex items-center justify-center">
                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <div className="font-display text-[15px] leading-tight text-sand-800">
                  {t.title}
                </div>
                <div className="mt-1 text-[13px] leading-[1.6] text-sand-600">
                  {t.desc}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// STATS + QUOTE gemeinsam
function StatsQuotePage({ page }) {
  return (
    <div className="flex-1 flex flex-col p-8 md:p-12">
      <PageHeader chapter={page.chapter} heading={page.heading} />
      <div className="grid grid-cols-2 gap-2.5 mb-6">
        {page.stats.map(([label, value], i) => (
          <div key={i} className="rounded-lg bg-white border border-cream-300 px-3 py-2.5">
            <div className="text-[9.5px] uppercase tracking-[0.2em] font-semibold text-sand-500 leading-tight">
              {label}
            </div>
            <div className="font-display text-[18px] md:text-[19px] text-brand-darkred leading-tight mt-1">
              {value}
            </div>
          </div>
        ))}
      </div>
      {page.quote && (
        <div className="mt-auto">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-terra mb-2">
            Aus der Praxis
          </div>
          <blockquote className="text-[13px] leading-[1.6] text-sand-700 italic">
            {page.quote}
          </blockquote>
          {page.author && (
            <div className="mt-2 text-[11px] text-sand-500">
              — {page.author}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// UPDATE / WHATSNEW · Bulletpoints
function UpdatePage({ page }) {
  return (
    <div className="flex-1 flex flex-col p-8 md:p-12">
      <PageHeader chapter={page.chapter} heading={page.heading} />
      {page.version && (
        <div className="inline-flex items-center gap-2 rounded-full bg-terra/10 text-terra px-2.5 h-6 text-[9.5px] uppercase tracking-[0.22em] font-bold self-start mb-4 border border-terra/20">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-terra animate-pulse" />
          {page.version}
        </div>
      )}
      <ul className="space-y-2.5">
        {page.bullets.map((b, i) => (
          <li key={i} className="flex gap-2.5">
            <span className="mt-[9px] inline-block h-1 w-1 rounded-full bg-terra shrink-0" />
            <span className="text-[13.5px] leading-[1.65] text-sand-700">{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// CTA · Rückumschlag, ruhig, klar
function CtaPage({ slug, kicker }) {
  return (
    <div className="flex-1 flex flex-col justify-between p-8 md:p-12 bg-gradient-to-br from-brand-rose/25 via-peach-50 to-cream-50 text-sand-800 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-terra/10 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-rose/25 blur-3xl" />

      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-terra">
          Vollständiger Leitfaden
        </div>
        <div className="mt-3 h-px w-10 bg-terra/70" />
      </div>

      <div className="relative">
        <h2 className="font-display text-[22px] md:text-[28px] leading-[1.2] text-sand-800 mb-4">
          Möchtest du den <span className="text-terra italic">kompletten Leitfaden?</span>
        </h2>
        <p className="text-[13.5px] leading-[1.7] text-sand-700 max-w-md">
          Trage deine E-Mail-Adresse ein und wir schicken dir die vollständige PDF-Ausgabe
          sofort zu — mit allen Checklisten, Formulierungen und Vorlagen. Kostenlos, ohne
          Newsletter-Zwang.
        </p>
        <Link
          to={`/leitfaden/${slug}`}
          data-testid="book-reader-cta"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-terra text-white hover:bg-terra-dark px-6 h-11 text-[13px] font-semibold transition-colors shadow-soft"
        >
          Jetzt anfordern <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="relative text-[10px] uppercase tracking-[0.28em] font-semibold text-sand-500">
        {kicker}
      </div>
    </div>
  );
}
